/**
 * B01 / H09-TECH — fetch public seller legal identity (APPROVED only).
 * Not shippingLightEnabled-gated. Not Wave1 Home H09-UI.
 */
import {
  useQuery,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError, type ApiClient } from '../../shared/api/ApiClient.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { isReservedCustomerPathSegment } from '../auth/customerAuthPaths.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import type { PublicSellerLegalDTO } from './publicSellerLegalTypes.js';

export type PublicSellerLegalQueryResult = UseQueryResult<PublicSellerLegalDTO | null, Error>;

export type SellerLegalAgeGateSnapshot =
  | { readonly resolved: true; readonly reviewRisk: string | null }
  | { readonly resolved: false };

async function loadPublicSellerLegal(
  api: ApiClient,
  tenantCode: string,
): Promise<PublicSellerLegalDTO | null> {
  try {
    return await api.request<PublicSellerLegalDTO>('/customer/seller-legal', {
      tenantCode,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Browse / cross-tenant age-gate — settle seller-legal for an offer tenant (404 → resolved null).
 * Network/other errors → unresolved (fail-closed).
 */
export async function fetchSellerLegalAgeGateSnapshot(
  queryClient: QueryClient,
  api: ApiClient,
  tenantCode: string,
): Promise<SellerLegalAgeGateSnapshot> {
  const code = tenantCode.trim();
  if (
    code.length === 0 ||
    isPlatformChromeTenant(code) ||
    isReservedCustomerPathSegment(code) ||
    !hasCustomerApiTenant(code)
  ) {
    return { resolved: true, reviewRisk: null };
  }
  try {
    const data = await queryClient.fetchQuery({
      queryKey: customerQueryKeys.sellerLegal(code),
      queryFn: () => loadPublicSellerLegal(api, code),
      staleTime: 60_000,
    });
    return { resolved: true, reviewRisk: data?.reviewRisk ?? null };
  } catch {
    return { resolved: false };
  }
}

/**
 * @param apiTenantCode — optional override (offer detail may be cross-tenant on browse).
 */
export function usePublicSellerLegalQuery(
  apiTenantCode?: string | null,
): PublicSellerLegalQueryResult {
  const api = useApiClient();
  const routeTenantCode = useTenantCode();
  const tenantCode =
    typeof apiTenantCode === 'string' && apiTenantCode.trim().length > 0
      ? apiTenantCode.trim()
      : routeTenantCode;

  return useQuery({
    queryKey: customerQueryKeys.sellerLegal(tenantCode),
    queryFn: async (): Promise<PublicSellerLegalDTO | null> => {
      try {
        return await api.request<PublicSellerLegalDTO>('/customer/seller-legal', {
          tenantCode,
        });
      } catch (err) {
        // APPROVED-only: missing / not approved → hide shell (not an error state).
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled:
      !isPlatformChromeTenant(tenantCode) &&
      !isReservedCustomerPathSegment(tenantCode) &&
      hasCustomerApiTenant(tenantCode),
    staleTime: 60_000,
    retry: 1,
  });
}
